/* ── A date as a person types it (the hospital list round, item 161) ─────

   A storekeeper reads an expiry off a carton and an invoice date off a
   supplier's paper, and types it the way the hospital writes dates. Four
   ways are offered, because those are the four the hospitals named:

     DD/MM/YYYY   day first, slashes (Kenya, Uganda and most of the world)
     MM/DD/YYYY   month first (a supplier writing the American way)
     YYYY-MM-DD   year first (the ISO way, which cannot be misread)
     DD.MM.YYYY   day first, dots

   Whatever the format, a day is stored the same way: an ISO day
   (YYYY-MM-DD) on the wire. What a format decides is only how a typed day
   is READ and how a day is WRITTEN back as numbers. The field repeats the
   day with its month in words ("3 Apr 2027") as it is typed, so a day and
   a month cannot be swapped without the person seeing it.

   Reading is forgiving where it can be and never guesses where it cannot:
   any of / . - or a space separates the parts; a year typed first (four
   digits) is always year, month, day, whatever the format; two digits for
   the year mean this century; eight digits with no separators are read in
   the format's own order. An expiry may be typed as a month ("04/2027" or
   "04/27"), which means the last day of that month, the way a carton
   printing EXP 04/2027 means it.

   The mirror of `apps/api/src/dates/typed-date.ts`; the API and the browser
   share no package, so `apps/api/src/dates/typed-date.test.ts` runs both
   over the same days and fails on the first that reads differently. Change
   one, change the other in the same commit. */

export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD.MM.YYYY';

export const DATE_FORMATS: readonly DateFormat[] = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD.MM.YYYY'];

/** The format a hospital that never chose writes dates in. */
export const DEFAULT_DATE_FORMAT: DateFormat = 'DD/MM/YYYY';

export function isDateFormat(value: unknown): value is DateFormat {
  return typeof value === 'string' && (DATE_FORMATS as readonly string[]).includes(value);
}

/** What each format looks like written out, for a picker: "31/12/2027". */
export const DATE_FORMAT_EXAMPLE: Record<DateFormat, string> = {
  'DD/MM/YYYY': '31/12/2027',
  'MM/DD/YYYY': '12/31/2027',
  'YYYY-MM-DD': '2027-12-31',
  'DD.MM.YYYY': '31.12.2027',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export type TypedDate =
  /* Nothing typed. */
  | { kind: 'empty' }
  /* A whole day, or a month standing for its last day. */
  | { kind: 'day'; iso: string; monthOnly: boolean }
  /* Something that is not a date yet: still being typed, or impossible. */
  | { kind: 'invalid'; reason: string; incomplete: boolean };

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

function isoOf(year: number, month: number, day: number): string {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

/** A two-digit year is this century's: 27 is 2027. */
function fullYear(text: string): number {
  const n = Number(text);
  return text.length <= 2 ? 2000 + n : n;
}

type Order = 'dmy' | 'mdy' | 'ymd';

function orderOf(format: DateFormat): Order {
  if (format === 'MM/DD/YYYY') return 'mdy';
  if (format === 'YYYY-MM-DD') return 'ymd';
  return 'dmy';
}

function checked(year: number, month: number, day: number, monthOnly: boolean): TypedDate {
  if (!Number.isInteger(year) || year < 1900 || year > 2199) {
    return { kind: 'invalid', reason: 'That year is not one a date here can carry', incomplete: false };
  }
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return { kind: 'invalid', reason: `There is no month ${month}. Months run from 1 to 12`, incomplete: false };
  }
  const last = daysInMonth(year, month);
  if (monthOnly) return { kind: 'day', iso: isoOf(year, month, last), monthOnly: true };
  if (!Number.isInteger(day) || day < 1 || day > last) {
    return { kind: 'invalid', reason: `${MONTH_NAMES[month - 1]} ${year} has ${last} days`, incomplete: false };
  }
  return { kind: 'day', iso: isoOf(year, month, day), monthOnly: false };
}

/** Read what a person typed, in the hospital's (or their own) format.
 *  `month: true` lets a month alone stand for its last day (an expiry). */
export function readTypedDate(text: string, format: DateFormat, opts: { month?: boolean } = {}): TypedDate {
  const raw = text.trim();
  if (raw === '') return { kind: 'empty' };
  const order = orderOf(format);

  /* Eight digits and nothing else: the format's own order, no separators. */
  if (/^\d{8}$/.test(raw)) {
    const a = raw.slice(0, 2);
    const b = raw.slice(2, 4);
    if (order === 'ymd') return checked(Number(raw.slice(0, 4)), Number(raw.slice(4, 6)), Number(raw.slice(6, 8)), false);
    if (order === 'mdy') return checked(Number(raw.slice(4)), Number(a), Number(b), false);
    return checked(Number(raw.slice(4)), Number(b), Number(a), false);
  }

  const parts = raw.split(/[\s/.\-]+/).filter(Boolean);
  if (parts.some((p) => !/^\d+$/.test(p))) {
    return { kind: 'invalid', reason: 'A date is numbers and separators only', incomplete: false };
  }

  /* A month alone, for an expiry: MM/YYYY, MM/YY or YYYY-MM. */
  if (opts.month && parts.length === 2) {
    const [p, q] = parts;
    if (p.length === 4 && q.length <= 2) return checked(Number(p), Number(q), 0, true);
    if (p.length <= 2 && (q.length === 4 || q.length === 2)) return checked(fullYear(q), Number(p), 0, true);
    return { kind: 'invalid', reason: 'Not a date yet', incomplete: true };
  }

  if (parts.length < 3) return { kind: 'invalid', reason: 'Not a date yet', incomplete: true };
  if (parts.length > 3) return { kind: 'invalid', reason: 'A date has a day, a month and a year', incomplete: false };
  const [p, q, r] = parts;

  /* A year typed first is a year whatever the format: 2027-04-03 is the
     third of April in every format, which is why ISO cannot be misread. */
  if (p.length === 4) return checked(Number(p), Number(q), Number(r), false);
  if (p.length > 2 || q.length > 2) return { kind: 'invalid', reason: 'A day and a month are one or two digits', incomplete: false };
  if (r.length === 1 || r.length === 3) return { kind: 'invalid', reason: 'Not a date yet', incomplete: true };
  if (r.length > 4) return { kind: 'invalid', reason: 'A year is four digits, or two', incomplete: false };
  const year = fullYear(r);
  /* Under YYYY-MM-DD a day typed first is still read day first, the way
     every country Meridian serves writes it; the month in words beside the
     field is what makes that visible. */
  if (order === 'mdy') return checked(year, Number(p), Number(q), false);
  return checked(year, Number(q), Number(p), false);
}

function partsOf(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

/** An ISO day written as numbers in a format: 2027-04-03 → "03/04/2027". */
export function writeDay(iso: string | null | undefined, format: DateFormat): string {
  if (!iso) return '';
  const parts = partsOf(iso);
  if (!parts) return iso;
  const { y, m, d } = parts;
  switch (format) {
    case 'MM/DD/YYYY':
      return `${pad(m)}/${pad(d)}/${pad(y, 4)}`;
    case 'YYYY-MM-DD':
      return `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
    case 'DD.MM.YYYY':
      return `${pad(d)}.${pad(m)}.${pad(y, 4)}`;
    default:
      return `${pad(d)}/${pad(m)}/${pad(y, 4)}`;
  }
}

/** An ISO day with its month in words: 2027-04-03 → "3 Apr 2027". */
export function dayInWords(iso: string | null | undefined): string {
  if (!iso) return '';
  const parts = partsOf(iso);
  if (!parts || parts.m < 1 || parts.m > 12) return iso ?? '';
  return `${parts.d} ${MONTHS[parts.m - 1]} ${parts.y}`;
}

/** A month in words: 2027-04-30 → "Apr 2027". */
export function monthInWords(iso: string | null | undefined): string {
  if (!iso) return '';
  const parts = partsOf(iso);
  if (!parts || parts.m < 1 || parts.m > 12) return iso ?? '';
  return `${MONTHS[parts.m - 1]} ${parts.y}`;
}

/** The placeholder a field shows for a format: "DD/MM/YYYY", or with a
 *  month allowed "DD/MM/YYYY or MM/YYYY". */
export function formatHint(format: DateFormat, opts: { month?: boolean } = {}): string {
  if (!opts.month) return format;
  const month = format === 'YYYY-MM-DD' ? 'YYYY-MM' : format === 'DD.MM.YYYY' ? 'MM.YYYY' : 'MM/YYYY';
  return `${format} or ${month}`;
}
